// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format } from 'date-fns';

// Material UI ( mui )  components
import Box from '@mui/material/Box';

// Section for import custom components --------------------------------------------------------------------------------
import { RentMarketPriceHistoryContext } from '@providers/MansionDetail/RentMarketPriceHistory/RentMarketPriceHistoryProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import { RentMarketPriceHistoryItem } from '@/types/components//MansionDetail/RentMarketPriceHistoryPropsData';
import '@styles/mansions/detail.scss';

type ChartProps = {
  legendState: {
    series1: boolean;
    series2: boolean;
    series3: boolean;
    series4: boolean;
  };
  toggleLegendState: (seriesKey: keyof ChartProps['legendState']) => void;
  averageSquareMeter: number;
};

export default function Chart({ legendState, toggleLegendState, averageSquareMeter }: ChartProps) {
  const { RentMarketPriceHistoryData } = useContext(RentMarketPriceHistoryContext);
  let marketPriceHistories;
  if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryMansions &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryMansions === 'object'
  ) {
    marketPriceHistories = RentMarketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories;
  } else if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryCities &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryCities === 'object'
  ) {
    marketPriceHistories = RentMarketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories;
  } else if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryTowns &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryTowns === 'object'
  ) {
    marketPriceHistories = RentMarketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories;
  } else if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryStations &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryStations === 'object'
  ) {
    marketPriceHistories = RentMarketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories;
  }

  let dates: string[] = [];
  if (marketPriceHistories && typeof marketPriceHistories === 'object') {
    dates = Object.keys(marketPriceHistories);
  }

  const getTsuboData = (dataObject: { [date: string]: RentMarketPriceHistoryItem }) => {
    // console.log(dataObject);
    return dates.map((date) => {
      if (!dataObject[date]) {
        return 0;
      }
      const price = dataObject[date].pricePerTsubo ? dataObject[date].pricePerTsubo : 0;
      return price;
    });
  };

  const series = [];
  if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryMansions &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryMansions === 'object'
  ) {
    if (typeof RentMarketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories === 'object') {
      series.push({
        name: `赤線 = ${RentMarketPriceHistoryData.marketPriceHistoryMansions.mansionName}の賃料相場`,
        className: 'mansions',
        visible: legendState.series1,
        data: getTsuboData(RentMarketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories),
        lineWidth: 4,
        color: '#FF3C00',
        zIndex: 4,
        events: {
          legendItemClick() {
            toggleLegendState('series1');
            return false;
          },
        },
      });
    }
  }

  if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryCities &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryCities === 'object'
  ) {
    if (typeof RentMarketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories === 'object') {
      series.push({
        name: `青線 = ${RentMarketPriceHistoryData.marketPriceHistoryCities.cityName}の賃料相場`,
        className: 'cities',
        visible: legendState.series2,
        data: getTsuboData(RentMarketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories),
        lineWidth: 4,
        color: '#00B4FF',
        dashStyle: 'ShortDot',
        zIndex: 3,
        events: {
          legendItemClick() {
            toggleLegendState('series2');
            return false;
          },
        },
      });
    }
  }

  if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryTowns &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryTowns === 'object'
  ) {
    if (typeof RentMarketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories === 'object') {
      series.push({
        name: `紫線 = ${RentMarketPriceHistoryData.marketPriceHistoryTowns.townName}の賃料相場`,
        className: 'towns',
        visible: legendState.series3,
        data: getTsuboData(RentMarketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories),
        lineWidth: 4,
        color: '#C800FF',
        dashStyle: 'ShortDot',
        zIndex: 2,
        events: {
          legendItemClick() {
            toggleLegendState('series3');
            return false;
          },
        },
      });
    }
  }
  if (
    typeof RentMarketPriceHistoryData === 'object' &&
    RentMarketPriceHistoryData.marketPriceHistoryStations &&
    typeof RentMarketPriceHistoryData.marketPriceHistoryStations === 'object'
  ) {
    if (typeof RentMarketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories === 'object') {
      series.push({
        name: `緑線 = ${RentMarketPriceHistoryData.marketPriceHistoryStations.stationName}の賃料相場`,
        className: 'stations',
        visible: legendState.series4,
        data: getTsuboData(RentMarketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories),
        lineWidth: 4,
        color: '#37C80A',
        dashStyle: 'ShortDot',
        zIndex: 1,
        events: {
          legendItemClick() {
            toggleLegendState('series4');
            return false;
          },
        },
      });
    }
  }

  const options = {
    chart: {
      height: 450,
      type: 'line',
      spacing: [0, 0, 0, 0], // チャート要素の余白を調整
      style: {
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'", // ここでグローバルなフォントファミリーを設定
      },
    },
    tooltip: {
      enabled: true, // tooltipを有効にする
      shared: false, // 共有tooltipを有効にする（複数のシリーズがある場合に便利）
      useHTML: true, // HTMLタグをtooltip内で使用可能にする
      style: {
        color: '#191919', // テキストの色
        backgroundColor: '#FFF9AC', // 背景色
        fontSize: '10px',
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
      },
      formatter() {
        // この関数内で `this` はtooltipを表示しているポイントを指します。
        const { point }: any = this; // 現在のポイントにアクセス
        const xAxisLabel: string = point.category; // x軸のラベル（年月）を取得
        const squareMeter: number = ((point.y / 3.30579) * averageSquareMeter) / 10000; // y軸の値を坪数に変換
        // tooltipのHTMLをカスタマイズ
        return `<b>${xAxisLabel}<br/>${squareMeter.toFixed(1)}万円<br>(${point.y.toFixed(0)}円/坪)</b>`;
      },
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: dates.map((date) => format(new Date(date), 'yyyy年MM月')),
      labels: {
        rotation: 300, // ラベルを水平に表示
        style: {
          fontSize: '10px',
          fontFamily:
            "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
        },
      },
    },
    yAxis: [
      {
        title: {
          text: '',
        },
        labels: {
          formatter(): string {
            // 価格を坪数に変換（1坪 = 3.30579平方メートル）
            const context: any = this;
            const squareMeter = ((context.value / 3.30579) * averageSquareMeter) / 10000;
            // ラベルのフォーマットを定義
            return `${squareMeter.toFixed(1)}万円<br><span style="font-size: 9px;">(${context.value}円/坪)</span>`;
          },
          style: {
            fontSize: '10px',
            fontFamily:
              "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
          },
        },
        opposite: false,
      },
    ],
    legend: {
      itemStyle: {
        fontSize: '14px',
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
      },
      layout: 'horizontal', // 水平に項目を並べる
      align: 'center', // 中央揃え
      verticalAlign: 'top', // 上部に配置
      borderWidth: 0, // 凡例のボーダー（オプション）
      margin: 0, // 凡例のマージンを設定
      padding: 24, // 凡例内のパディングを設定
      y: -10, // 凡例を上に10px移動させる
      itemMarginTop: 10, // 判例項目間の上の余白を調整
      itemMarginBottom: 10, // 判例項目間の下の余白を調整
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: true,
      },
    },
    series,
  };

  return (
    <Box className="w-full px-5">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}
